import * as UI from "@iyk/ui"

import { Toast as BaseToast } from "@base-ui/react/toast"
import { toastManager, useToast, type Toast } from "../hooks/useToast.tsx"

function ToastProvider({ children, ...props }: React.ComponentProps<typeof BaseToast.Provider>) {
  return (
    <BaseToast.Provider toastManager={toastManager} timeout={4000} {...props}>
      {children}
      <ToastList />
    </BaseToast.Provider>
  )
}

function ToastList() {
  const { toasts } = useToast()

  return (
    <BaseToast.Portal data-slot="toast-portal">
      <BaseToast.Viewport
        className="light fixed top-auto right-4 bottom-4 z-toast mx-auto flex w-[calc(100%_-_2rem)] sm:right-8 sm:bottom-8 sm:w-[356px]"
        data-slot="toast-viewport"
      >
        {toasts.map((toast) => (
          <BaseToast.Root
            key={toast.id}
            toast={toast}
            swipeDirection={["right", "down"]}
            data-slot="toast"
            className={UI.cx(
              "absolute right-0 bottom-0 left-auto z-[calc(1000-var(--toast-index))] mr-0 flex w-full items-center justify-between gap-1.5 rounded bg-clip-padding p-4 shadow-lg transition-all [transition-property:opacity,transform] duration-200 ease-out select-none",
              "after:absolute after:bottom-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-['']", // Fill the gap between toasts
              "[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)+calc(min(var(--toast-index),10)*-1*var(--gap))))_scale(calc(max(0,1-(var(--toast-index)*0.1))))]", // Initial position and scale
              "data-[expanded]:[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-offset-y)*-1+calc(var(--toast-index)*var(--gap)*-1)+var(--toast-swipe-movement-y)))]", // Expanded state animation
              "data-[ending-style]:opacity-0 data-[limited]:opacity-0 data-[starting-style]:[transform:translateY(150%)] data-[starting-style]:opacity-0 data-[ending-style]:[&:not([data-limited])]:[transform:translateY(150%)]",
              "data-[ending-style]:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))] data-[expanded]:data-[ending-style]:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]", // Right swipe animation
              "data-[ending-style]:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))] data-[expanded]:data-[ending-style]:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]", // Down swipe animation
              toast.type === "error"
                ? "bg-tomato-3-light text-tomato-11-light"
                : "bg-gray-1-light text-gray-12-light"
            )}
            style={{
              ["--gap" as string]: "0.8rem",
              ["--offset-y" as string]:
                "calc(var(--toast-offset-y) * -1 + (var(--toast-index) * var(--gap) * -1) + var(--toast-swipe-movement-y))"
            }}
          >
            <div className="flex items-center gap-2">
              {toast.type !== "default" && (
                <div
                  className="shrink-0 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
                  data-slot="toast-icon"
                >
                  {TOAST_ICONS[toast.type]}
                </div>
              )}

              <div className="flex flex-col">
                <BaseToast.Title className="text-sm" data-slot="toast-title" />
                <BaseToast.Description
                  className={UI.cx(
                    "text-sm",
                    toast.type === "error" ? "text-tomato-10-light" : "text-gray-11-light"
                  )}
                  data-slot="toast-description"
                />
              </div>
            </div>
            {toast.action && toast.action(toast)}
          </BaseToast.Root>
        ))}
      </BaseToast.Viewport>
    </BaseToast.Portal>
  )
}

export const ToastAction = ({
  children,
  toastType,
  ...props
}: React.HTMLAttributes<HTMLButtonElement> & {
  toastType: Toast.Type
}) => {
  const kind = toastType === "error" ? "danger" : "shaded"

  return (
    <UI.Button {...props} kind={kind} size="sm" type="button">
      {children}
    </UI.Button>
  )
}

const TOAST_ICONS: Record<Toast.Type, React.ReactNode> = {
  default: null,
  loading: <UI.Icon.LoadingSpinner />,
  success: <UI.Icon.CheckCircledFilled />,
  error: <UI.Icon.Error />,
  info: <UI.Icon.InfoCircled />
}

export { ToastProvider }
